import { useCallback } from 'react'
import { useSnackbar } from 'notistack'
import { t } from '@lingui/macro'

import { parseMultipleDocumentsMetadata, base64ToFile } from '../lib/xmlParser'
import { useSavePatientFile } from './useSavePatientFile'

export function useDmpDocumentsNotification(patientId: string) {
  const { saveFile } = useSavePatientFile(patientId)
  const { enqueueSnackbar } = useSnackbar()

  const onDmpDocumentsNotification = useCallback(
    async (xmlData: string) => {
      try {
        const documents = parseMultipleDocumentsMetadata(xmlData)
        console.log('[DmpDocuments] Received:', documents.length, 'document(s)')

        if (documents.length === 0) {
          throw new Error('No documents found in notification')
        }

        let savedCount = 0
        const errors: string[] = []

        for (const metadata of documents) {
          try {
            console.log('[DmpDocuments] Processing document:', {
              uniqueId: metadata.uniqueId,
              title: metadata.title,
              typeCode: metadata.typeCode,
              format: metadata.format,
            })

            const file = base64ToFile(metadata.contentBase64, metadata.title, metadata.format)
            await saveFile(file)
            savedCount++
            console.log('[DmpDocuments] Document saved successfully:', metadata.title)
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error'
            console.error('[DmpDocuments] Failed to save document:', metadata.title, message)
            errors.push(`${metadata.title}: ${message}`)
          }
        }

        if (savedCount > 0) {
          enqueueSnackbar(t`${savedCount} document(s) added successfully`, { variant: 'success' })
        }
        if (errors.length > 0) {
          enqueueSnackbar(t`Failed to save ${errors.length} document(s)`, { variant: 'error' })
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown error'
        console.error('[DmpDocuments] Failed to process documents:', message)
        enqueueSnackbar(t`Failed to save documents: ${message}`, { variant: 'error' })
      }
    },
    [saveFile, enqueueSnackbar]
  )

  return { onDmpDocumentsNotification }
}
